import { useCallback, useEffect, useMemo, useState } from "react";
import { Button as UIButton } from "@mui/material";
import { Link } from "react-router-dom";
import Edit2LineIcon from "remixicon-react/Edit2LineIcon";
import BuildingLineIcon from "remixicon-react/BuildingLineIcon";
import clsx from "clsx";
import { Text, InfoContainer } from "$components";
import { Company, Warehouse } from "$types";
import useCompany from "$hooks/useCompany";
import useJStyles from "$hooks/useJStyles";
import { getObjectFromArray, toTime } from "$utils/utils";
import useAuth from "$hooks/useAuth";

import useStyles from "./useStyles";
import { queryClient } from "$utils/queryClient";
import { CompanyServices } from "$services";
import { CacheInvalidationAction } from "$utils/cache";

type Props = {
  entrepot: Warehouse;
  onEditClick: () => void;
};

function EntrepotInfo({ entrepot, onEditClick }: Props) {
  const classes = useStyles();
  const { jclasses } = useJStyles();
  const { isAdmin } = useAuth();
  const { isClient, isContributor, isLogistician } = useCompany();
  const [allCompanies, setCompanies] = useState([]);
  const {
    companies,
    delivery,
    location: { adress, complement, city, country, cp },
    parentCompany: parentCompanyId,
    isManageable,
  } = entrepot || { location: {} };
  const hours = [
    ...(delivery?.am || []).map((d) => toTime(d)),
    ...(delivery?.pm || []).map((d) => toTime(d)),
  ];

  const fecthCompanies = useCallback(async () => {
    if (companies?.length > 0) {
      // Get all ids
      const listId = [
        ...new Set(
          companies
            .map((company: any) => company._id)
            .flat()
            .filter((f) => f)
        ),
      ];

      const filters = {
        $inId: listId,
      };
      let getCompanies;
      getCompanies = await queryClient.getQueryData([
        CacheInvalidationAction.GET_COMPANIES,
        filters,
      ]);
      if (!getCompanies) {
        getCompanies = await queryClient.fetchQuery(
          [CacheInvalidationAction.GET_COMPANIES, filters],
          () => CompanyServices.getCompanies(filters),
          { staleTime: 60 * 1000 }
        );
        getCompanies = getCompanies.data;
        await queryClient.setQueryData(
          [CacheInvalidationAction.GET_COMPANIES, filters],
          getCompanies
        );
      }
      setCompanies(getCompanies);
    }
  }, [setCompanies, companies]);

  useEffect(() => {
    fecthCompanies();
  }, [fecthCompanies]);

  const parentCompany = getObjectFromArray(allCompanies, parentCompanyId);
  const { companyTypes } = parentCompany || {};

  const managedCompanies = companies?.reduce((managedCompanies, c) => {
    if (!c.isManageable) {
      return managedCompanies;
    }
    const fullCompany = allCompanies.find((c2) => c2._id === c._id);
    return [...managedCompanies, ...(fullCompany ? [fullCompany] : [])];
  }, []);

  const notManagedCompanies = companies?.reduce((notManagedCompanies, c) => {
    if (c.isManageable) {
      return notManagedCompanies;
    }
    const fullCompany = allCompanies.find((c2) => c2._id === c._id);
    return [...notManagedCompanies, ...(fullCompany ? [fullCompany] : [])];
  }, []);

  const addressBlock = (
    <>
      {adress}
      <br />
      {complement && (
        <>
          {complement}
          <br />
        </>
      )}
      {cp} {city}
      <br />
      {country}
    </>
  );

  const formattedHours = useMemo(() => {
    const times = [...(delivery?.am || []), ...(delivery?.pm || [])];
    let horaire = times.sort((a: number, b: number) => a - b);
    horaire = Array.from(new Set(horaire));
    const list: any[] = [];
    let item: any[] = [];
    horaire.forEach((a, i) => {
      if (i === 0) {
        item.push(a);
      } else {
        if (a - item[item.length - 1] <= 0.5) {
          item.push(a);
          if (i === horaire.length - 1) {
            const val = Array.from(
              new Set([toTime(item[0]), toTime(item[item.length - 1] + 0.5)])
            );
            list.push(val.join(" - "));
          }
        }
        if (a - item[item.length - 1] > 0.5) {
          let val = Array.from(
            new Set([toTime(item[0]), toTime(item[item.length - 1] + 0.5)])
          );
          list.push(val.join(" - "));
          list.push(" / ");
          item = [];
          item.push(a);
          if (i === horaire.length - 1) {
            val = Array.from(new Set([toTime(a), toTime(a + 0.5)]));
            list.push(val.join(" - "));
          }
        }
      }
    });
    return list.join("");
  }, [delivery?.am, delivery?.pm]);

  const renderCompanies = (companies: Company[]) =>
    companies.map((company, idx) => (
      <>
        <Link
          to={`/entreprise/${company._id}`}
          className={jclasses.ghostLink}
          key={company._id}
        >
          {company.name}
        </Link>
        {idx !== companies.length - 1 && " | "}
      </>
    ));

  const manageStockLabel = `Gestion de stock ${
    isContributor(companyTypes) ? "en tant qu'intervenant" : ""
  }${isLogistician(companyTypes) ? "en tant que logisticien" : ""}`;

  return (
    <InfoContainer
      className={clsx(classes.topBlock, classes.warehouseInfo)}
      leftHeader={
        <div className={classes.infoHeader}>
          <BuildingLineIcon />
          <Text type="h3">Informations entrepôt</Text>
        </div>
      }
      rightHeader={
        <UIButton className={classes.editButton} onClick={onEditClick}>
          <Edit2LineIcon />
        </UIButton>
      }
    >
      <div className="about">
        <div className="left">
          <table>
            <tr>
              <td className="label">Nom de l&apos;entrepôt</td>
              <td className="data">{entrepot?.name}</td>
            </tr>
            {isLogistician(companyTypes) && (
              <tr>
                <td className="label">Nom du logisticien</td>
                <td className="data">{parentCompany?.name}</td>
              </tr>
            )}
            <tr>
              <td className="label">Horaires de livraison</td>
              <td className="data">{hours.length ? formattedHours : "N/A"}</td>
            </tr>

            {isAdmin() && (
              <>
                {/* Client warehouse stocks are globally managed by directly looking at isManageable */}
                {isClient(companyTypes) && (
                  <tr>
                    <td className="label">
                      Gestion de stock en tant que client
                    </td>
                    <td className="data">
                      {isManageable === true ? "OUI" : "NON"}
                    </td>
                  </tr>
                )}
                {(isContributor(companyTypes) ||
                  isLogistician(companyTypes)) && (
                  <>
                    {managedCompanies?.length > 0 && (
                      <tr>
                        <td className="label">{manageStockLabel}</td>
                        <td className="data">
                          <div className="isManageableBlock">
                            <span className="isManageableLabel">OUI</span>
                            <span>{renderCompanies(managedCompanies)}</span>
                          </div>
                        </td>
                      </tr>
                    )}
                    {notManagedCompanies?.length > 0 && (
                      <tr>
                        <td className="label">
                          {/* Display title only if not displayed before in "managedCompanies" block */}
                          {managedCompanies?.length === 0 && manageStockLabel}
                        </td>
                        <td className="data">
                          <div className="isManageableBlock">
                            <span className="isManageableLabel">NON</span>
                            <span>{renderCompanies(notManagedCompanies)}</span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </>
            )}
          </table>
        </div>
        <div className="right">
          <table>
            <tbody>
              <tr>
                <td className="label">Adresse</td>
                <td className="data">{addressBlock}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </InfoContainer>
  );
}

export default EntrepotInfo;
